import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Create,
  Business,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../contexts/CompanyContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { selectedCompany, companies } = useCompany();
  const { user } = useAuth();
  const [stats, setStats] = useState({
    contentCount: 0,
    imageCount: 0,
    loading: true,
  });

  useEffect(() => {
    const fetchStats = async () => {
      if (!selectedCompany) {
        setStats({ contentCount: 0, imageCount: 0, loading: false });
        return;
      }

      try {
        const response = await axios.get(`/api/stats?companyId=${selectedCompany.id}`);
        setStats({
          contentCount: response.data.contentCount || 0,
          imageCount: response.data.imageCount || 0,
          loading: false,
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
        setStats({ contentCount: 0, imageCount: 0, loading: false });
      }
    };

    fetchStats();
  }, [selectedCompany]);

  const dashboardStats = [
    {
      title: 'Your Companies',
      value: companies.length,
      icon: <Business />,
      color: '#1976d2',
    },
    {
      title: 'Content Pieces',
      value: stats.loading ? <CircularProgress size={20} /> : stats.contentCount,
      icon: <Create />,
      color: '#388e3c',
    },
    {
      title: 'Images Generated',
      value: stats.loading ? <CircularProgress size={20} /> : stats.imageCount,
      icon: <ImageIcon />,
      color: '#f57c00',
    },
  ];

  const quickActions = [
    {
      title: 'Create Content',
      description: 'Generate AI-powered content for your company',
      action: () => navigate('/content'),
      color: 'primary',
    },
    {
      title: 'Manage Knowledge',
      description: 'Update your company knowledge base',
      action: () => navigate('/knowledge'),
      color: 'secondary',
    },
    {
      title: 'View Settings',
      description: 'Configure your account and preferences',
      action: () => navigate('/settings'),
      color: 'info',
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      
      {selectedCompany && (
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {selectedCompany.name} - {selectedCompany.industry}
        </Typography>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {dashboardStats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      backgroundColor: stat.color,
                      color: 'white',
                      mr: 2,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="h4" component="div">
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No recent activity to display. Start by creating some content!
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {quickActions.map((action, index) => (
                <Card key={index} variant="outlined">
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {action.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {action.description}
                    </Typography>
                    <Button
                      size="small"
                      variant="contained"
                      color={action.color as any}
                      onClick={action.action}
                    >
                      Go
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;

